import { defineSlotRecipe } from "@pandacss/dev";

/**
 * This recipe is only used in the field recipes, to apply the common styles (container borders, etc).
 * It shouldn't expose in preset.
 */
export const fieldDefaultRecipeDefinition = defineSlotRecipe({
  className: "aroma-container",
  jsx: ["div"],
  slots: ["container"],
  base: {
    container: {
      display: "flex",
      alignItems: "baseline",
      outline: "none",
      borderWidth: {
        base: "m",
        _disabled: "inherit",
      },
      borderStyle: {
        base: "solid",
        _disabled: "inherit",
      },
      borderColor: {
        base: "border.neutral.bold",
        _disabled: "inherit",
      },
      padding: "padding.3",
      gap: "gap.2",
      width: "100%",
      position: "relative",
      bg: {
        base: "background.neutral.base.subtlest",
        _hover: "background.neutral.base.subtlest-hover",
        _disabled: "background.neutral.disable.subtle",
      },
      color: {
        base: "content.neutral.base.subtle",
        _disabled: "content.neutral.base.disable",
      },
      _disabled: {
        _aromaticonChildren: {
          _required: {
            color: "content.neutral.base.disable",
          },
        },
      },
      transition: "all ease-in-out .2s",
      _aromaticonChildren: {
        alignSelf: "center",
        fontSize: "{sizes.xs}",
        _required: {
          fontSize: "{sizes.4xs}",
          position: "absolute",
          bg: "background.neutral.base.subtlest",
          color: "content.neutral.base.subtlest",
          top: 0,
          right: 0,
          shadow: "0 0 0 2px {colors.background.neutral.base.subtlest}",
          borderRadius: "pill",
        },
      },
    },
  },
  variants: {
    size: {
      small: {
        container: {
          padding: {
            base: "calc({spacing.padding.1} + {borderWidths.m})",
            _disabled: "calc({spacing.padding.1} + {borderWidths.m} * 2)",
          },
          paddingX: "padding.3",
          gap: "gap.2",
          borderRadius: "s",
        },
      },
      medium: {
        container: {
          paddingY: {
            base: "calc({spacing.padding.2} + {borderWidths.m})",
            _disabled: "calc({spacing.padding.2} + {borderWidths.m} * 2)",
          },
          borderRadius: "l",
        },
      },
    },
    colorTheme: {
      ai: {
        container: {
          borderColor: "transparent",
          _aromaticonChildren: {
            bg: "background.ai.bold-hover",
            color: "transparent",
            backgroundClip: "text",
          },
          _before: {
            content: '""',
            position: "absolute",
            inset: "-1px",
            padding: "1px",
            borderRadius: "m",
            background: "{colors.border.ai-hover}",
            WebkitMask:
              "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
            WebkitMaskComposite: "xor",
            maskComposite: "exclude",
            pointerEvents: "none",
          },
        },
      },
    },
    status: {
      error: {
        container: {
          borderColor: "border.functional.error",
          _aromaticonChildren: {
            _required: {
              color: "content.functional.error",
            },
          },
        },
      },
      warning: {
        container: {
          borderColor: "border.functional.warning",
          _aromaticonChildren: {
            _required: {
              color: "content.functional.warning",
            },
          },
        },
      },
      readOnly: {
        container: {
          bg: "background.neutral.base.subtler",
          borderColor: "transparent",
          color: "content.neutral.base.bold",
        },
        input: {
          color: "content.neutral.base.bold",
        },
      },
    },
  },
  compoundVariants: [
    {
      colorTheme: "ai",
      status: "error",
      css: {
        container: {
          borderColor: "border.functional.error",
          _before: {
            background: "transparent",
          },
          _aromaticonChildren: {
            bg: "transparent",
            color: "content.functional.error",
          },
        },
      },
    },
    {
      colorTheme: "ai",
      status: "warning",
      css: {
        container: {
          borderColor: "border.functional.warning",
          _before: {
            background: "transparent",
          },
          _aromaticonChildren: {
            bg: "transparent",
            color: "content.functional.warning",
          },
        },
      },
    },
    {
      colorTheme: "ai",
      status: "readOnly",
      css: {
        container: {
          borderColor: "transparent",
          _before: {
            background: "transparent",
          },
          _aromaticonChildren: {
            bg: "transparent",
            color: "content.neutral.base.bold",
          },
        },
      },
    },
  ],
  defaultVariants: {
    size: "small",
  },
});
